import store from '@/store'
import router from '@/router'
import EventBus from "@/event-bus"
import Alert from '@/components/modal/Alert.vue'
import UserInfoRegistGuid from '@/components/modal/UserInfoRegistGuid.vue'
import { createApp, markRaw } from "vue"
import jleageTicket from '@/mixins/jleageTicket'

// api呼び出し
//   permissionは配列、中身は以下の形式であること
//    エラータイプ（数値のみ) or {type:エラータイプ, func: callback関数}
//    ※callback関数の引数はエラータイプによって異なります。
//    ※エラータイプの説明
//      1: エラー画面         エラータイプのみの指定可
//      2: とどまる           エラータイプのみの指定可
//      3: 席種選択画面
//      4: チケット一覧画面
//      5: Jチケトップ        エラータイプのみの指定可
//      6: カード情報入力画面
//      7: 会員情報入力画面    エラータイプのみの指定可
//      8: 不足情報案内画面    エラータイプのみの指定可
//      9: とどまる（エラーメッセージをポップアップ表示)
export function callApi(path, param, permission=[]){
  store.commit('Loading/countUp')
  return store.dispatch(path, param)
    .then(response => response)
    .catch(error => {
      if(permission) error.permission = permission
      return Promise.reject(error)
    }).finally(()=>store.commit('Loading/countDown'))
}

// api呼び出し失敗
export async function finallyCallApi(error, next){
  store.commit('Loading/countUp')
  try{
    // 定義されている通信異常以外の異常
    if(!error) return replaceError(error, next)
    if(!error.response) return replaceError(error, next)
    if(!error.response.headers) return replaceError(error, next)
    if(!error.response.headers["content-type"]) return replaceError(error, next)
    if(error.response.headers["content-type"].indexOf('application/json') < 0) return replaceError(error, next)
    if(error.response.status != 400 && error.response.status != 503) return replaceError(error, next)
    if(!error.response.data) return replaceError(error, next)
    if(error.response.status == 503) return replaceMaintenance(error, next)
    if(!error.permission) return replaceError(error, next)
    if(!(error.permission instanceof Array)) return replaceError(error, next)
    if(error.permission.length < 1) return replaceError(error, next)

    // permissionチェック
    // ここでエラーになるのは製造ミス
    const types = error.permission.map(value=>{
      // 2:とどまる, 5:Jチケトップ, 7: 会員情報入力画面, 8: 不足情報案内画面
      if(value == 2 || value == 5 || value == 7 || value == 8) return value
      // 2,5以外ならcallback関数が必要
      if(!value.func) throw Error(`permission error ${value}`)
      // 共通レスポンス以外の定義はエラー
      if(![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(value.type)) throw Error(`permission error ${value}`)
      return value.type
    })

    if(!error.response.data.errInfo) return replaceError(error, next)
    const errInfo = error.response.data.errInfo
    // 予定外のerrInfo.typeが来た場合エラー
    if(!types.includes(errInfo.type)) return replaceError(error, next)

    // errInfo.typeにあった動作情報取得
    const typeInfo = error.permission.find(value => value.type == errInfo.type)
    const func = typeInfo ? typeInfo.func : undefined

    // エラー画面
    if(errInfo.type == 1){
      if(func) func()
      return replaceError(error, next)
    }

    // とどまる
    if(errInfo.type == 2){
      store.commit('Message/saveError', errInfo.message)
      window.scrollTo(window.scrollX, 0) // エラーメッセージが見えるようスクロールをtopに戻す
      if(func) func()
      if(next) next()
      return
    }

    // エラーメッセージは表示しない為削除
    store.commit('Message/saveError', [])

    // 会員情報登録
    if(errInfo.type == 7){
      // エラーメッセージをダイアログで表示する
      const openUserInfoRegistGuid = (next) => new Promise((resolve) => {
        if(next) store.commit("Loading/isRouting", false)
        store.commit('Loading/countDown')
        EventBus.$emit('openModal', markRaw(UserInfoRegistGuid),
        ()=>{},
        {
          close: () => {
            if(next) store.commit("Loading/isRouting", true)
            store.commit('Loading/countUp')
            resolve()
          }
        })
      })
      await openUserInfoRegistGuid(next)
      if(func) func()
      if(next) next()
      return
    }

    // 不足情報案内画面 
    if(errInfo.type == 8){
      if(func) func()
      if(next) next()
      return router.push({name: 'missing_info'})
    } 

    // エラーメッセージをダイアログで表示する
    const openAlert = (errInfo, next) => new Promise((resolve) => {
      if(next) store.commit("Loading/isRouting", false)
      store.commit('Loading/countDown')
      EventBus.$emit('openModal', markRaw(Alert),
      () => ({
        messages: errInfo.message
      }),
      {
        yes: () => {
          if(next) store.commit("Loading/isRouting", true)
          store.commit('Loading/countUp')
          resolve()
        }
      })
    })
    await openAlert(errInfo, next)

    switch(errInfo.type){
      case 5: // 5:Jチケトップ
        if(func) func()
        location.href = replaceTicketTop('/')
        break
      case 3: // 3:席種選択へ
      case 4: // 4:チケット一覧へ
      case 6: // 6:カード入力へ
      case 9: // 9: とどまる（エラーメッセージをポップアップ表示)
        func(next)
    }
  }catch(ex){
    replaceError(ex, next)
  }finally{ store.commit('Loading/countDown') }
}

function replaceError(error, next){
  // エラー画面遷移時の共通処理を行う
  const func = getCallBackFun(error, 1)
  store.commit('Message/saveError', [])
  if(func) func()
  // エラー画面に遷移
  if(next) return next(error)
  console.error(error)
  router.replace({name: 'error'})
}

// エラー種別ごとのCallBack関数を取得する
function getCallBackFun(error, type){
  if(!error) return undefined
  if(!error.permission) return undefined
  if(!(error.permission instanceof Array)) return undefined

  const errInfo = error.permission
    .find(value => value.type == type)

  if(!errInfo) return undefined
  return errInfo.func
}

function replaceMaintenance(error, next){
  //エラー画面遷移時の共通処理を行う
  const func = getCallBackFun(error, 1)
  store.commit('Message/saveError', [])
  if(func) func()
  // メンテナンス画面に遷移
  store.commit('RouteParams/setParams', error.response.data )
  if(next) return next({name: 'maintenance', replace: true})
  console.error(error)
  router.replace({ name: 'maintenance' })
}

function replaceTicketTop(url){
  return createApp({ mixins: [jleageTicket] }).mount("#mixin").jticketUrl(url)
}